import './MediaItem.scss';
import React, { useState } from 'react';
import classNames from 'classnames';
import { useMiwiStore, generalStore, searchStore } from '../../store';
import { useTranslation } from 'react-i18next';
import helpers from '../../js/helpers';
import ItemCard from '../ItemCard/ItemCard';
import Flag from '../flag/Flag';
import Config, { isVolkswagen, isBentley } from '../../js/config/_global';
import { RESPONSE, OVERRIDE_SISSION_DURATION } from '../../js/_constants';
import globeIcon from '@icons/languages-small.svg';
import { DeviceType } from '@org-avp/device-models';
import AiComplianceIcon from '../aiComplianceIcon/aiComplianceIcon';

type MediaData = {
    [key: string]: any;
    '@type': string;
    uuid: string;
    id: string;
    title: string;
    description: string;
    thumbnail: string;
    duration?: number;
    language?: string;
    type?: string;
    sourceFormats?: unknown;
    fileInfo?: {
        fileSize?: number;
    };
    kvps?: string[];
    sharedCountries?: string[];
    createdAt?: string;
};

type MediaItemProps = {
    name: string;
    data: any;
    onAddIconClick: (item: any) => void;
};

// custom icon for bentley
let iconButtonActiveClassSelected = 'system-select-selected-large';
if (isBentley) {
    iconButtonActiveClassSelected = 'system-select-selected-large-bentley';
}

const MediaItem = ({
    name,
    data,
    onAddIconClick,
}: MediaItemProps) => {
    const { t } = useTranslation();
    const {
        overrideDeviceId,
        overrideDeviceType,
        overrideMediaUrl,
        setAsset2D,
        controlMedia2D,
        setAsset3D,
        setOverride,
        setOverrideMediaUrl,
        setOverrideMedia,
    } = useMiwiStore();
    const { setSelectedMediaItems, selectedMediaItems, gridStyle } = generalStore();
    const { doSearch, search } = searchStore();
    const [isImageLoading, setIsImageLoading] = useState(false);
    const [isSelected, setIsSelected] = useState(
        selectedMediaItems.map(item => item[RESPONSE.ID]).includes(data[RESPONSE.ID])
    );

    const toggleIsModalOpen = () => {
        onAddIconClick(data);
    };

    const toggleSelection = () => {
        let selectedMedia = selectedMediaItems;
        if (!isSelected) {
            selectedMedia = [...selectedMediaItems, data];
        } else {
            selectedMedia = selectedMedia.filter(item => item.uuid !== data.uuid);
        }

        setSelectedMediaItems(selectedMedia);
        setIsSelected(!isSelected);
    };

    const onClick = () => {
        localStorage.setItem('mediaData', JSON.stringify(data));
    };

    const handleImageLoad = () => {
        setIsImageLoading(false);
    };

    const connectToMiwi = async (media: MediaData) => {
        const type = media['@type'].toLowerCase();
        media.type = type;
        const source = helpers.getMediaSource(media?.sourceFormats);
        const sessionTimeout = type === 'image' ? OVERRIDE_SISSION_DURATION : (media.duration || 0) * 1000;
        if (overrideDeviceType === DeviceType.CHROMEBOX) {
            await setAsset2D(type as 'image' | 'video', source, media as any);
            if (type === 'image') {
                Config.overrideSessionTimeout && clearTimeout(Config.overrideSessionTimeout);
                Config.overrideSessionTimeout = setTimeout(async () => {
                    await controlMedia2D('SKIP');
                }, sessionTimeout);
            }
        } else if (overrideDeviceType === DeviceType.AVE) {
            await setAsset3D(type, source, media as any);
        } else if (overrideDeviceType === 'NeRD') {
            await setOverride(media as any, type, source);
            Config.overrideSessionTimeout && clearTimeout(Config.overrideSessionTimeout);
            Config.overrideSessionTimeout = setTimeout(async () => {
                setOverrideMediaUrl('');
                setOverrideMedia('');
            }, sessionTimeout);
        }
    };

    const generateActionButtons = () => {
        const selectionIconClasses = classNames({
            'system-select-large': !isSelected,
            [iconButtonActiveClassSelected]: isSelected,
        });

        if (data.type === 'document') {
            return [
                {
                    icon:
                        overrideMediaUrl === helpers.getMediaSource(data.sourceFormats)
                            ? 'system-display-large-white'
                            : 'system-display-large',
                    onClick: () => connectToMiwi(data),
                    disabled: !overrideDeviceId || helpers.getAssetLicenceStatus(data) === 'is-expired',
                    isActive: overrideMediaUrl === helpers.getMediaSource(data.sourceFormats),
                },
            ];
        }

        const actionButtons = [
            {
                icon: selectionIconClasses,
                onClick: toggleSelection,
                disabled: helpers.getAssetLicenceStatus(data) === 'is-expired',
                name: `select-${name}-button`,
            },
            {
                icon:
                    overrideMediaUrl === helpers.getMediaSource(data.sourceFormats)
                        ? 'system-display-large-white'
                        : 'system-display-large',
                onClick: () => connectToMiwi(data),
                disabled:
                    !overrideDeviceId ||
                    helpers.getAssetLicenceStatus(data) === 'is-expired' ||
                    !helpers.getMediaSource(data?.sourceFormats),
                isActive: overrideMediaUrl === helpers.getMediaSource(data.sourceFormats),
                name: `display-${name}-button`,
            },
            {
                icon: 'system-add-large',
                onClick: toggleIsModalOpen,
                disabled: helpers.getAssetLicenceStatus(data) === 'is-expired',
                isActive: false,
                name: `add-${name}-to-a-playlist-button`,
            },
        ];

        return actionButtons;
    };

    const renderUsageTo = () => {
        const licenceStatus = helpers.getAssetLicenceStatus(data);
        const validity = classNames('valid-until', licenceStatus);

        const renderNoRestrictions = () => `${t('media_item_valid')} : ${t('no_restrictions')}`;
        const renderValidTill = () => `${t('media_item_valid')}: ${helpers.getExpirationDate(data)} ${t('hour')}`;
        const renderExpired = () => `${t('media_item_not_valid')}: ${helpers.getExpirationDate(data)} ${t('hour')}`;

        return (
            <>
                <p className={validity}>
                    {licenceStatus === 'not-expired' && renderValidTill()}
                    {licenceStatus === 'is-expired' && renderExpired()}
                    {licenceStatus === 'no-restrictions' && renderNoRestrictions()}
                </p>
                {data.kvps && data.kvps.length > 0 && (
                    <div className="kvps-tags">
                        {data.kvps.map(ele => (
                            <span
                                key={ele}
                                className="kvps-tag"
                                onClick={() =>
                                    doSearch({
                                        ...search,
                                        page: 1,
                                        // @ts-ignore
                                        kvps: [ele],
                                    })
                                }
                            >
                                {ele}
                            </span>
                        ))}
                    </div>
                )}
            </>
        );
    };

    const renderFlags = (countries: string[]) => {
        if (countries.length <= 5) {
            return countries.map(country => country.split('/')[2]).map(country => <Flag key={country} country={country} />);
        }

        return (
            <>
                {countries
                    .slice(0, 5)
                    .map(country => country.split('/')[2])
                    .map(country => <Flag key={country} country={country} />)}
                {'...'}
            </>
        );
    };

    const renderCountries = (countries: string[]) =>
        countries[0] === '???' ? (
            <img src={globeIcon} className="b-item-card__glob-icon" alt="global" />
        ) : (
            renderFlags(countries)
        );

    const renderSubtitle = () =>
        data['@type'].toLowerCase() === 'video' ? (
            <>
                {`${data.language ? data.language.toUpperCase() : 'EN'} - ${helpers.humanDuration(
                    data.duration
                )} ${isVolkswagen ? 'Min' : `Min - ${helpers.bytesToSize(data.fileInfo?.fileSize)}`}`}

                {data.sharedCountries && data.sharedCountries.length > 0 && (
                    <> - {renderCountries(data.sharedCountries)}</>
                )}
            </>
        ) : (
            <>
                {data.language ? data.language.toUpperCase() : 'EN'} -
                {helpers.bytesToSize(data.fileInfo?.fileSize)} -
                {data.sharedCountries &&
                    data.sharedCountries.length > 0 &&
                    renderCountries(data.sharedCountries)}
            </>
        );

    return (
        <div className="b-media-item">
            <ItemCard
                buttons={generateActionButtons()}
                description={data.description}
                footer={renderUsageTo()}
                href={{
                    pathname: `/${data['@type'].toLowerCase()}s/${data.uuid}`,
                    query: { id: data.id },
                }}
                icon={`media-${data['@type'].toLowerCase()}`}
                overlayTag={
                    <>
                        <AiComplianceIcon aiCompliance={data.aiCompliance} />
                        {helpers.isRecentlyAdded(data.createdAt) ? (
                            <span className="overlay-tag">{t('recently_added')}</span>
                        ) : null}
                    </>
                }
                isGridStyle={gridStyle === 'grid'}
                isLoading={isImageLoading}
                key={data[RESPONSE.ID]}
                onClick={onClick}
                onImageLoad={handleImageLoad}
                subtitle={renderSubtitle()}
                thumbnail={data.thumbnail}
                videoSource={
                    data['@type'].toLowerCase() === 'video' && helpers.getMediaSource(data.sourceFormats)
                }
                title={data.title}
                type={data['@type'].toLowerCase()}
                name={name}
            />
        </div>
    );
};

export default MediaItem;